import { useTranslation } from 'react-i18next'

export type DonateOrg = {
  // translation keys: help.<key>.name and help.<key>.desc
  key: string
  img: string
  donateUrl: string
  socialLinks: { asset: string; labelKey: string; href: string }[]
  // translation key of a note shown under the description, e.g. what to write as the donation reference
  noteKey?: string
  // translation key replacing help.<key>.desc, when the organisation is shown for a different cause
  descKey?: string
}

// Donate section: a card per organisation with logo, donate button, description and social links.
// `introKey`: translation key of a line under the heading saying what these organisations do.
export function Donate({ orgs, introKey }: { orgs: DonateOrg[]; introKey?: string }) {
  const { t } = useTranslation()

  return (
    <div className='flex flex-col gap-8 w-full max-w-5xl'>
      <div className='flex flex-col gap-3'>
        <h2 className='text-2xl font-bold'>{t('help.donate')}</h2>
        {introKey && <p className='opacity-70'>{t(introKey)}</p>}
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-8 w-full'>
        {orgs.map(({ key, img, donateUrl, socialLinks, noteKey, descKey }) => (
          <div key={key} className='flex flex-col gap-4 bg-white p-6 rounded-lg shadow-sm'>
            <div className='flex items-stretch gap-3 w-full'>
              <img
                src={img}
                alt={t(`help.${key}.name`)}
                className='flex-1 min-w-0 h-32 object-contain rounded bg-gray-100'
              />
              <a
                href={donateUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='shrink-0 bg-blue-700 text-white px-4 rounded-md font-bold hover:bg-blue-800 transition flex items-center justify-center'
              >
                {t('help.donate')}
              </a>
            </div>
            <h3 className='text-xl font-semibold'>{t(`help.${key}.name`)}</h3>
            <p className='text-base opacity-80'>{t(descKey ?? `help.${key}.desc`)}</p>
            {noteKey && (
              <p className='rounded-md bg-yellow-100 px-3 py-2 text-base font-semibold'>
                {t(noteKey)}
              </p>
            )}
            <div className='flex gap-3 mt-auto'>
              {socialLinks.map(({ asset, labelKey, href }) => {
                const label = t(labelKey)
                return (
                  <a
                    key={labelKey}
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={label}
                    className='opacity-100 hover:opacity-50 transition'
                  >
                    <img src={asset} alt={label} className='h-6 w-6' />
                  </a>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
